import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Trash2, RefreshCw, FileText, CheckCircle2, AlertCircle } from "lucide-react";

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / BYTES_PER_MB;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / BYTES_PER_KB).toFixed(0)} KB`;
}

function UploadLabel({ isUploading, temEnviado }) {
  if (isUploading) return <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />Enviando...</>;
  if (temEnviado) return <><RefreshCw className="w-3.5 h-3.5 mr-1" />Substituir</>;
  return <><Upload className="w-3.5 h-3.5 mr-1" />Enviar</>;
}

function AnexoItem({ doc, deleting, onDownload, onDelete }) {
  return (
    <li
      className="flex items-center justify-between text-xs bg-white rounded p-2 border border-slate-200"
      data-testid={`anexo-item-${doc.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        <span className="truncate max-w-[300px]" title={doc.filename}>{doc.filename}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-500">{fmtSize(doc.size)}</span>
        {doc.uploaded_at && (
          <>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{new Date(doc.uploaded_at).toLocaleString("pt-BR")}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button size="sm" variant="ghost" onClick={() => onDownload(doc)} title="Baixar / Visualizar" data-testid={`btn-download-${doc.id}`}>
          <Download className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          disabled={deleting === doc.id}
          onClick={() => onDelete(doc)}
          title="Excluir documento"
          data-testid={`btn-delete-${doc.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </li>
  );
}

export function AnexoRow({ tipo, enviados, isUploading, deleting, onUpload, onDownload, onDelete }) {
  const temEnviado = enviados.length > 0;
  const inputId = `upload-${tipo.key}`;
  return (
    <div
      className={`border rounded p-3 transition-colors ${temEnviado ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}
      data-testid={`anexo-row-${tipo.key}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-[200px]">
          {temEnviado
            ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            : <AlertCircle className="w-4 h-4 text-slate-400" />}
          <span className="font-semibold text-sm">{tipo.label}</span>
          <Badge
            variant="secondary"
            className={temEnviado ? "bg-emerald-100 text-emerald-800 text-[10px]" : "bg-slate-100 text-slate-600 text-[10px]"}
          >
            {temEnviado ? `${enviados.length} arquivo(s)` : "Pendente"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            id={inputId}
            className="hidden"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => { onUpload(tipo.key, e.target.files?.[0]); e.target.value = ""; }}
            data-testid={`file-input-${tipo.key}`}
          />
          <label htmlFor={inputId}>
            <Button
              size="sm"
              variant={temEnviado ? "outline" : "default"}
              className={temEnviado ? "" : "bg-[#083CA6] hover:bg-[#062f85]"}
              disabled={isUploading}
              asChild
            >
              <span data-testid={`btn-upload-${tipo.key}`}>
                <UploadLabel isUploading={isUploading} temEnviado={temEnviado} />
              </span>
            </Button>
          </label>
        </div>
      </div>

      {temEnviado && (
        <ul className="mt-2 space-y-1">
          {enviados.map((doc) => (
            <AnexoItem key={doc.id} doc={doc} deleting={deleting} onDownload={onDownload} onDelete={onDelete} />
          ))}
        </ul>
      )}
    </div>
  );
}
